// @flow

import React from 'react';
import styled, {
  /* $FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.104.0. To view the error, delete this comment and run Flow. */
  type ReactComponentStyled,
  withTheme,
} from 'styled-components';
import {
  padding,
  colors,
  fontSize,
  fontWeight,
  ReskinThemes,
} from '../constants';
import { XIcon } from '../icons';
import { azaleaTheme, birchTheme } from './chips_theme';

type Props = {
  /** A valid maxWidth css value. e.g. "80px" or "50%" */
  maxWidth?: string,
  onRemove?: Function,
  readonly?: boolean,
  status: 'error' | 'selected' | 'unavailable' | 'default',
  text: string,
  title?: string,
  /** Controls if background changes on hover */
  hoverable?: boolean,
  theme?: { name: string },
};

const stopEvent = (event: SyntheticEvent<HTMLElement, MouseEvent>) =>
  event.stopPropagation();

const statusTheme = (flavor, themeName) => {
  const statusMapping =
    themeName !== ReskinThemes.BIRCH ? azaleaTheme : birchTheme;
  return statusMapping[flavor] || {};
};

const Chip = withTheme((props: Props) => {
  const { onRemove, ...nonFunctionProps } = props;
  const maxWidth = props.maxWidth || '100%';
  const hoverColor =
    props.readonly && props.theme?.name !== ReskinThemes.BIRCH
      ? 'initial'
      : statusTheme(props.status, props.theme?.name).hoverColor;

  return (
    <ChipWrapper
      {...nonFunctionProps}
      onMouseDown={stopEvent}
      maxWidth={maxWidth}
      hoverable={props.hoverable === undefined ? true : props.hoverable}
      hoverColor={hoverColor}
    >
      <ChipText {...nonFunctionProps} onMouseDown={stopEvent}>
        {props.text}
      </ChipText>
      <ChipRemoveLink>
        {!props.readonly && (
          <CloseIcon
            size={16}
            onClick={props.onRemove}
            onMouseDown={props.onRemove}
            status={props.status}
            theme={props.theme}
          />
        )}
      </ChipRemoveLink>
    </ChipWrapper>
  );
});

type WrapperAttributes = {
  'data-test-role': string,
};

const ChipWrapper: ReactComponentStyled<WrapperAttributes> = styled.span.attrs(
  (props: Props) => ({
    'data-test-role': 'react-chosen-multiselect-option',
    title: props.title || props.text,
  })
)`
  display: inline-block;
  background: ${(props) =>
    statusTheme(props.status, props.theme?.name).backgroundColor};
  border: 1px solid
    ${(props) => statusTheme(props.status, props.theme?.name).borderColor};
  color: ${(props) => statusTheme(props.status, props.theme?.name).textColor};
  font-size: ${fontSize.small};
  font-weight: ${fontWeight.normal};
  border-radius: ${(props) =>
    props.theme?.name !== ReskinThemes.BIRCH ? `100` : `5`}px;
  vertical-align: middle;
  padding: 2px 12px;

  // spacing between chips especially between rows.
  margin: 2.5px;

  ${(props) =>
    props.hoverable &&
    `
    &:hover,
    &:active,
    &:focus {
      background: ${props.hoverColor};
      border-color: ${
        statusTheme(props.status, props.theme?.name).hoverBorderColor
      };
    }
  `}
`;

const ChipText = styled.span`
  display: inline-block;
  text-decoration: ${(props) =>
    props.status === 'unavailable' ? 'line-through' : 'initial'};
  max-width: ${(props) => props.maxWidth};
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
  vertical-align: inherit;
`;

const ChipRemoveLink = styled(ChipText).attrs({
  'data-test-role': 'react-remove-option',
})`
  padding-left: ${padding.tiny};
  color: ${colors.darkestGrey};
  margin-right: -6px;
  min-height: 20px;
`;

ChipRemoveLink.displayName = 'ChipRemoveLink';

const CloseIcon = styled(XIcon)`
  padding: 2px;
  path {
    fill: ${(props) => statusTheme(props.status, props.theme?.name).iconColor};
  }

  &:hover {
    path {
      fill: ${(props) =>
        statusTheme(props.status, props.theme?.name).iconHoverColor};
    }
    background: ${(props) =>
      statusTheme(props.status, props.theme?.name).iconHoverBGColor};
    border-radius: 50%;
  }
`;

CloseIcon.displayName = 'CloseIcon';

export { Chip };
